import { Fragment, useState } from "react";
import { NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { SquaresFour, Wallet, UserGear, Target, Sliders, PiggyBank, ChartLine, BookOpenText, List, SignOut, Receipt, Bank, Coins, CalendarCheck, ListChecks, Calculator, ArrowSquareOut, ChartBar, Images } from "@phosphor-icons/react";
import { OnboardingProvider, TakeTourButton } from "@/components/Onboarding";
import { PAYROLL_URL } from "@/components/PayrollCalcCard";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { SiteFooter } from "@/components/SiteFooter";
import { AdRail } from "@/components/AdRail";
import { TspUpdateButton } from "@/components/TspUpdate";
import { TspPlaidLauncher } from "@/components/TspAutoSync";

export function TopLinks() {
  const cls = ({ isActive }) => `text-sm font-medium transition-colors ${isActive ? "text-cyan-700 dark:text-cyan-400" : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"}`;
  return (
    <nav className="flex items-center gap-4 sm:gap-6" data-testid="top-menu">
      <NavLink to="/about" className={cls} data-testid="top-about-link">About</NavLink>
      <NavLink to="/why-free" className={cls} data-testid="top-why-free-link">Why Free?</NavLink>
      <NavLink to="/contact" className={`${cls({ isActive: false })} hidden sm:inline`} data-testid="top-contact-link">Contact</NavLink>
    </nav>
  );
}

export const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour },
  { to: "/accounts", label: "Accounts & Sync", icon: Wallet },
  { to: "/profile", label: "Profile & Pay", icon: UserGear },
  { to: "/paychecks", label: "Paycheck Ledger", icon: Receipt },
  { to: "/goals", label: "Goal Tracker", icon: Target },
  { to: "/pension", label: "FERS Pension", icon: Bank },
  { to: "/best-date", label: "Best Retire Date", icon: CalendarCheck },
  { to: "/checklist", label: "Retirement Checklist", icon: ListChecks },
  { to: "/income", label: "Income Plan", icon: Coins },
  { to: "/simulator", label: "What-If Simulator", icon: Sliders },
  { to: "/ira", label: "IRA Center", icon: PiggyBank },
  { to: "/funds", label: "Fund Prices", icon: ChartLine },
  { to: "/learn", label: "Content Hub", icon: BookOpenText },
];

function NavItems({ onNavigate }) {
  const { user } = useAuth();
  return (
    <nav className="space-y-1">
      {NAV.map(({ to, label, icon: I }) => (
        <NavLink key={to} to={to} onClick={onNavigate} data-testid={`nav-${to.slice(1)}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-150 ${isActive ? "bg-slate-900 text-white dark:bg-cyan-400/15 dark:text-cyan-300" : "text-slate-600 hover:bg-slate-900/5 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white"}`}>
          <I size={18} /> {label}
        </NavLink>
      ))}
      <a href={PAYROLL_URL} target="_blank" rel="noopener" onClick={onNavigate} data-testid="nav-payroll-calculator"
        className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-150 text-slate-600 hover:bg-slate-900/5 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white">
        <Calculator size={18} /> Payroll Calculator <ArrowSquareOut size={12} className="ml-auto opacity-60" />
      </a>
      {user && <TakeTourButton testid="nav-take-tour" onClick={onNavigate} className="w-full" />}
      {user?.role === "admin" && (
        <NavLink to="/admin/referrals" onClick={onNavigate} data-testid="nav-admin-referrals"
          className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-150 ${isActive ? "bg-slate-900 text-white dark:bg-cyan-400/15 dark:text-cyan-300" : "text-slate-600 hover:bg-slate-900/5 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white"}`}>
          <ChartBar size={18} /> Admin: Referrals
        </NavLink>
      )}
      {user?.role === "admin" && (
        <NavLink to="/admin/screenshots" onClick={onNavigate} data-testid="nav-admin-screenshots"
          className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-150 ${isActive ? "bg-slate-900 text-white dark:bg-cyan-400/15 dark:text-cyan-300" : "text-slate-600 hover:bg-slate-900/5 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white"}`}>
          <Images size={18} /> Admin: Screenshots
        </NavLink>
      )}
    </nav>
  );
}

const Brand = () => (
  <NavLink to="/" className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900 dark:text-white" data-testid="brand-home-link">
    <img src="/pillarwealth-icon.png" alt="PillarWealth logo" className="h-9 w-9 rounded-lg" data-testid="brand-logo-img" />
    PillarWealth
  </NavLink>
);

export function AppShell({ publicMode = false }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  if (!publicMode && user === null) return <div className="min-h-screen grid place-items-center text-sm text-muted-foreground" data-testid="auth-loading">Loading your ledger...</div>;
  if (!publicMode && user === false) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  const Wrap = user ? OnboardingProvider : Fragment;
  return (
    <Wrap>
    <div className="relative min-h-screen">
      <div className="orb w-[460px] h-[460px] -top-32 left-1/4" style={{ background: "var(--orb-a)" }} />
      <div className="orb w-[400px] h-[400px] top-1/2 -right-20" style={{ background: "var(--orb-b)" }} />
      <div className="orb w-[320px] h-[320px] bottom-0 left-0" style={{ background: "var(--orb-c)" }} />
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col justify-between border-r border-border/70 p-5 z-20" style={{ background: "var(--glass-bg)", backdropFilter: "blur(16px)" }}>
        <div className="space-y-8"><Brand /><NavItems /></div>
        {user && (
          <div className="flex items-center gap-3 border-t border-border pt-4">
            {user.picture ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full" /> : <span className="h-8 w-8 rounded-full bg-cyan-600 text-white grid place-items-center text-xs">{user.name?.[0]}</span>}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={logout} aria-label="Sign out" data-testid="logout-button"><SignOut size={18} /></Button>
          </div>
        )}
      </aside>
      <div className="relative z-10 lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-4 border-b border-border/70 px-4 sm:px-8 h-16" style={{ background: "var(--glass-bg)", backdropFilter: "blur(20px)" }}>
          <div className="flex items-center gap-3 lg:hidden min-w-0">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild><Button variant="ghost" size="icon" data-testid="mobile-nav-button"><List size={20} /></Button></SheetTrigger>
              <SheetContent side="left" className="w-72 space-y-8"><Brand /><NavItems onNavigate={() => setOpen(false)} />
                {user && <Button variant="outline" className="w-full" onClick={logout} data-testid="mobile-logout-button">Sign out</Button>}
                {!user && <NavLink to="/login" className="block text-sm text-cyan-700">Sign in</NavLink>}
              </SheetContent>
            </Sheet>
            <span className="hidden sm:block"><Brand /></span>
          </div>
          <div className="hidden lg:flex items-center gap-6"><TopLinks /><p className="text-xs text-muted-foreground font-mono-num">TSP prices settle nightly ~7–9 PM ET</p></div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="lg:hidden"><TopLinks /></div>
            {user && <TspUpdateButton size="sm" label="Update TSP" testid="header-tsp-update-button" className="h-8 px-3" />}
            {user && <TspPlaidLauncher />}
            {!user && <NavLink to="/login" className="text-sm font-medium text-cyan-700 dark:text-cyan-400" data-testid="header-signin-link">Sign in</NavLink>}
            <ThemeToggle />
          </div>
        </header>
        <div className="flex"><main className="px-4 sm:px-8 py-8 max-w-[1500px] flex-1 min-w-0"><Outlet /><SiteFooter /></main><AdRail /></div>
      </div>
    </div>
    </Wrap>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 fade-up">
      <div>
        <h1 className="text-3xl sm:text-4xl font-semibold text-grad-head" data-testid="page-title">{title}</h1>
        {subtitle && <p className="mt-2 text-sm md:text-base text-slate-600 dark:text-slate-400 max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Panel({ title, action, children, className = "", testid }) {
  return (
    <section className={`glass glass-lift p-5 sm:p-6 fade-up ${className}`} data-testid={testid}>
      {(title || action) && (
        <div className="flex items-center justify-between gap-3 mb-4">
          {title && <h3 className="font-display text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}
